import { getAutoProps } from 'js/auto_props/client';

import { recordExtensionBucketing } from 'js/bulk_download/autotest/recordExtensionBucketing';
import type { BulkDownloadTrigger } from 'js/bulk_download/bulk_download_helper';
import { recordShow } from 'js/utils/dismissibles';

/**
 * The reader's arm of the post-pack fake door, or null for no funnel — meaning
 * the PDF Pack download shows the stock progress modal.
 *
 * THE WAVE IS CLOSED. Its YAML moved to `config/finished_ab_tests/`, so
 * `AbTest::TESTS` has no such key, the server resolves nil, that prop is absent
 * for everyone, and this returns null at the auto-prop read — before
 * `recordFunnelExposure`, so no bucketing is recorded either. The Redis dial
 * (`<team>:<test>:rollout`) decides nothing now. Nothing below runs again
 * until a NEW wave ships under a new key.
 *
 * The arm comes from the SERVER, in the `BulkDownloadFlags` auto-prop, because
 * the client-side `abTest()` helper has no rollout gate: it would bucket every
 * pack downloader the moment this ships, and this funnel replaces a screen
 * someone has just paid for. The server honours the staged rollout, so exposure
 * starts at nobody and is ramped deliberately.
 *
 * A PROP RATHER THAN AN ENDPOINT, which this used to be. `showBulkDownloadModal`
 * gates rendering the download modal on knowing the arm, so a fetch put a
 * network round trip in front of a paid-for download — bounded by a timeout
 * that still cost the reader up to a second of dead screen, and unbounded
 * before that. Reading a prop is synchronous, so that whole class of problem is
 * gone along with the endpoint, its controller and its route.
 *
 * The server resolves READ-ONLY on page render, so loading a page enrols
 * nobody, and this reader records nothing either: the bucketing is written when
 * the pack download actually STARTS (`PremiumWorkCardsBulkDownloadModal`, stage
 * `downloading`, through `recordFunnelExposure`), which is the moment the
 * funnel replaces. Opening the pack chooser and closing it enrols nobody.
 *
 * This module deliberately does NOT import the generated `abTest()`. That
 * function derives the variant in the browser, where there is no rollout
 * branch, so importing it would hand out arms regardless of the dial — the
 * parity problem `AbTestFrontendRolloutChecker` exists to catch.
 *
 * THAT GUARD DOES NOT COVER A FINISHED TEST. `staged_and_active?`
 * (`util/hooks/homegrown_checks`) needs the YAML under `config/ab_tests/` with
 * `active: true` AND `staged_rollout: true`, so this test is skipped and the
 * import could come back unnoticed — as it is for every finished test. The rule
 * stands anyway: it is the reason the arm is read from the auto-prop, and it
 * applies again the moment a new wave ships.
 */
const TEST_KEY =
  'demo_day_2026_q3_reference_manager_and_chrome_extension_fake_door';

/**
 * The server-side ledger that makes the pitch once-per-reader. Resolved by
 * `Dismissibles::Base.find`, so this string is the Ruby class name.
 */
const DISMISSIBLE_NAME = 'PostPackFakeDoor';

/**
 * The `?bulkDownload` return from checkout, which resumes the pack the reader
 * has just paid for (`useHeroDownloadState`, `WorkActions`).
 */
const CHECKOUT_RESUMPTION_TRIGGER = 'swp-auto-bulk-download';

/**
 * The controls that ask for a PDF PACK, and nothing else.
 *
 * THE SINGLE-PAPER DOWNLOAD IS NOT ON THIS LIST, DELIBERATELY. A premium
 * reader who clicks "Download PDF" is asking for the one paper in front of
 * them; `showBulkDownloadModal` routes them through the same premium modal, so
 * without this allowlist the funnel would replace the screen after a
 * single-paper download too and the wave would be measuring the wrong intent.
 * That excludes `swp-download-button` and every `*-work-card-download-button`
 * (including the heroes' own single-pdf trigger), plus the related-work rails
 * `swp-right-rail-related-work` and `swp-related-works-grid`, which download
 * the one related paper that was clicked.
 *
 * Nor is anything that opened BY ITSELF: `swp-auto-download` is the
 * interruption chooser and the email deep link, and
 * `swp-auto-bulk-download-books` is the BDB email redirect. A pack modal nobody
 * asked for is not a reader downloading a pack.
 *
 * The reg-path pack triggers (`sutd-bulk-download`, `sutd-post-download-pack`)
 * are also out: those pages never register `BulkDownloadFlags`, so there is no
 * arm to read there anyway, and their readers are not premium.
 */
const PACK_DOWNLOAD_TRIGGERS: Partial<Record<BulkDownloadTrigger, true>> = {
  'swp-bulk-download-button': true,
  'swp-bulk-download-preview-button': true,
  'swp-sticky-header-bulk-download-button': true,
  /*
   * The LISWP-blocks heroes (`hero_tabs`, `hero_itemized`) open the PACK modal
   * with their bare `heroSource` — `swp-tabs-hero` is the only live value —
   * while `useDownload` derives `swp-tabs-hero-work-card-download-button` for
   * the SINGLE pdf off the same hero. One belongs here and the other does not,
   * so they are listed apart rather than matched by prefix.
   */
  'swp-tabs-hero': true,
  'news-feed-bulk-download-button': true,
  'search-results-bulk-download-button': true,
  'library-bulk-download-button': true,
  'library-pdf-packs-card': true,
  'recommended-pdf-packs-card': true,
  'recommended-pdf-packs-deep-link': true,
};

/**
 * Local record that THIS READER has already been shown the funnel.
 *
 * The server's ledger (`Dismissibles::PostPackFakeDoor`) is authoritative and
 * crosses devices, but it only reaches the surfaces that call the
 * bulk-download counts endpoint. The /pdf_packs cards and the feed's pack card
 * open the modal without it, so a marker written here is what stops a SECOND
 * exposure on those surfaces, and a second one inside a single page load.
 *
 * SCOPED TO THE USER ID, because the ledger is per user and localStorage is
 * per browser. On a shared machine, or after an account switch, an unscoped
 * marker would suppress the funnel for the NEXT reader — who the server says
 * is allowed — and since the arm is never resolved for them they would not be
 * bucketed either. That is a silent, non-random hole in the population the
 * guardrails are read against, which is worse than the repeat it prevents.
 */
function shownKey(): string {
  return `post_pack_fake_door_shown:${window.Aedu?.User?.current?.id ?? 'anon'}`;
}

function alreadyShown(): boolean {
  try {
    return window.localStorage.getItem(shownKey()) === '1';
  } catch {
    /* Private-mode storage throws; fall back to the server's ledger alone. */
    return false;
  }
}

export type PackDownloadMoment = {
  /** Which surface opened the modal; see `BULK_DOWNLOAD_IMPRESSION_TRIGGERS`. */
  trigger?: string;
  /** `Aedu.User.current.premium`, baked in when the page renders. */
  isPremium: boolean;
  /** Server's post-upgrade grace window; see `BulkDownload::PostUpgradeAccess`. */
  postUpgradeAccess: boolean;
  /**
   * The counts endpoint's `post_pack_fake_door_allowed`. Absent on the
   * surfaces that never call it, which is why `alreadyShown` exists — absent
   * must not mean "suppress", or those surfaces would lose the funnel
   * entirely.
   */
  fakeDoorAllowed?: boolean;
};

/**
 * The arm to carry into this pack download, or null to leave the stock progress
 * modal alone. Records nothing, but it does READ the local marker, so two calls
 * in one page load can legitimately disagree.
 *
 * TWO MOMENTS, AND NOTHING ELSE. The condition that routes a reader to the
 * premium pack modal at all (`isPremium || purchased || postUpgradeAccess`) is
 * far wider than either of them: it takes in a three-year subscriber
 * auto-interrupted mid-read who asked for nothing, and a free reader
 * re-downloading a pack they bought six months ago. Bucketing those readers
 * spends the sample on intent this pitch was never aimed at, and dilutes every
 * guardrail read against the bucketed side.
 *
 * 1. POST-CHECKOUT — the resumption trigger above, or the server's 10-minute
 *    `postUpgradeAccess` window on one of the pack controls below. The window
 *    is what identifies the reader who has just paid: `premium` is baked into
 *    the page at render, so it is still false for them and moment 2 cannot
 *    catch them.
 * 2. A PREMIUM READER DOWNLOADING A PACK — premium, plus one of the pack
 *    controls in `PACK_DOWNLOAD_TRIGGERS`: the LISWP PDF Pack buttons, the
 *    sticky header, the work-card pack buttons and the /pdf_packs cards and
 *    deep links. NOT the single-paper "Download PDF" button.
 *
 * AND ONCE PER READER. The funnel replaces a screen someone has paid for, so
 * the pitch is shown on the FIRST qualifying pack download and never again:
 * the reader has answered the question, the metric is per-user, and a
 * decline-then-retry would only inflate the numerator against a denominator
 * that cannot grow. The server's ledger decides
 * (`post_pack_fake_door_allowed`), with a local marker covering the surfaces
 * that never ask it.
 *
 * A FORCED ARM IGNORES THE CAP. At a 100% dial most reviewers will have taken
 * an organic exposure already, and the `?ab_test[...]` override exists so QA
 * does not have to reach for `reset_show_count` in a console. The trigger and
 * moment gates still apply — the override picks the arm, not the surface.
 *
 * THE ALLOWLIST BINDS BOTH MOMENTS, which is why `postUpgradeAccess` is not
 * sufficient on its own. Four call sites now forward that flag, two of them
 * manual CTAs, and the authorization mirror it belongs to (`useDownload`, the
 * single-paper gate) would be a reasonable fifth — at which point an
 * unqualified post-upgrade branch would silently re-admit the single-paper
 * download this allowlist exists to keep out. The resumption trigger is
 * exempt because it IS the post-checkout pack download, under its own name.
 */
export function referenceManagerFunnelArm({
  trigger,
  isPremium,
  postUpgradeAccess,
  fakeDoorAllowed,
}: PackDownloadMoment): string | null {
  let flags;
  try {
    flags = getAutoProps('BulkDownloadFlags');
  } catch {
    /*
     * Most pages never register these flags and `getAutoProps` throws rather
     * than returning undefined there. Not an error for this reader: it just
     * means no funnel. Mirrors `quietExtensionVariant`.
     */
    return null;
  }

  const forced = !!flags?.postPackForced;
  if (!forced && (fakeDoorAllowed === false || alreadyShown())) return null;

  const packControl = Object.hasOwn(PACK_DOWNLOAD_TRIGGERS, trigger ?? '');
  const postCheckout =
    trigger === CHECKOUT_RESUMPTION_TRIGGER ||
    (postUpgradeAccess && packControl);
  const premiumPackDownload = isPremium && packControl;
  if (!postCheckout && !premiumPackDownload) return null;

  return flags?.postPackVariant ?? null;
}

/**
 * Record the exposure, at the start of the pack download the arm was resolved
 * for. `control` and `holdout` are recorded like any other arm — a denominator
 * needs them — and render the stock modal.
 *
 * ALSO CLOSES THE DOOR. `Dismissibles::PostPackFakeDoor` is what makes the
 * pitch once-per-reader, and it is spent HERE rather than when the pitch
 * renders: a reader whose zip fails saw the funnel take over their download
 * screen, and asking them again on the next pack would be the same
 * interruption twice. The local marker is written alongside it for the
 * surfaces that never read the server's answer.
 *
 * A FORCED ARM IS SHOWN AND NEVER RECORDED — not the bucketing, and not the
 * ledger either, so a reviewer walking the funnel with `?ab_test[...]` can do
 * it repeatedly. The override rides on the page request, so the bucketing POST
 * carries none of it: the server would find no override, pass the rollout
 * gate, disagree with the forced arm, raise a Sentry variant mismatch, and
 * write a user_tests row for traffic the readout excludes by joining to that
 * very table.
 */
export function recordFunnelExposure(arm: string): void {
  try {
    if (getAutoProps('BulkDownloadFlags')?.postPackForced) return;
  } catch {
    return;
  }

  recordExtensionBucketing(TEST_KEY, arm);

  try {
    window.localStorage.setItem(shownKey(), '1');
  } catch {
    /* Private mode; the server's ledger still closes the door. */
  }
  recordShow(DISMISSIBLE_NAME);
}
